<template>
  <v-app>
    <!-- Navigation Drawer for Mobile -->
    <v-navigation-drawer
      v-if="authStore.isAuthenticated || isSharedView"
      v-model="showMobileMenu"
      temporary
      location="left"
    >
      <v-list>
        <!-- Shared View Navigation -->
        <template v-if="isSharedView">
          <v-list-item
            @click="openPrintView"
            prepend-icon="mdi-printer"
            title="Print"
          ></v-list-item>
        </template>
        
        <!-- Authenticated User Navigation -->
        <template v-else>
          <v-list-item 
            @click="$router.push('/lists'); showMobileMenu = false" 
            prepend-icon="mdi-home"
            title="Lists"
          ></v-list-item>
          
          <v-list-item
            @click="showCreateListDialog = true; showMobileMenu = false"
            prepend-icon="mdi-plus"
            title="Create List"
          ></v-list-item>
          
          <v-divider></v-divider>
          
          <v-list-item
            @click="$router.push('/shares'); showMobileMenu = false"
            prepend-icon="mdi-share-variant"
            title="Shares"
          ></v-list-item>
          
          <v-list-item
            @click="showExportDialog = true; showMobileMenu = false"
            prepend-icon="mdi-download"
            title="Export Data"
          ></v-list-item>
          
          <v-list-item
            @click="showImportDialog = true; showMobileMenu = false"
            prepend-icon="mdi-upload"
            title="Import Data"
          ></v-list-item>
          
          <v-divider></v-divider>
          
          <v-list-item
            v-if="authStore.user?.role === 'ADMIN'"
            @click="$router.push('/admin'); showMobileMenu = false"
            prepend-icon="mdi-shield-account"
            title="Admin Panel"
          >
            <template v-slot:append>
              <v-badge content="A" color="warning" inline></v-badge>
            </template>
          </v-list-item>
          
          <v-list-item
            @click="$router.push('/profile'); showMobileMenu = false"
            prepend-icon="mdi-account"
            title="Profile"
          ></v-list-item>
          
          <v-list-item
            @click="handleMobileLogout"
            prepend-icon="mdi-logout"
            title="Logout"
          ></v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar app color="primary" dark>
      <!-- Mobile hamburger menu -->
      <v-app-bar-nav-icon 
        v-if="authStore.isAuthenticated || isSharedView" 
        @click="showMobileMenu = !showMobileMenu"
        class="d-flex d-md-none"
      ></v-app-bar-nav-icon>
      
      <v-app-bar-title @click="goToPacklists" style="cursor: pointer;">
        {{ getHeaderTitle }}
      </v-app-bar-title>
      <v-spacer></v-spacer>
      
      <!-- Desktop navigation for shared views -->
      <template v-if="isSharedView">
        <div class="d-none d-md-flex align-center">
          <v-btn
            text
            @click="openPrintView"
            prepend-icon="mdi-printer"
            class="mr-2"
          >
            Print
          </v-btn>
        </div>
      </template>
      
      <!-- Desktop navigation for authenticated users -->
      <template v-else-if="authStore.isAuthenticated">
        <div class="d-none d-md-flex align-center">
          <v-btn
            color="primary"
            @click="showCreateListDialog = true"
            prepend-icon="mdi-plus"
            class="mr-2"
          >
            Create List
          </v-btn>
          <v-btn
            text
            @click="$router.push('/shares')"
            prepend-icon="mdi-share-variant"
            class="mr-2"
          >
            Shares
          </v-btn>
          <v-btn
            text
            @click="showExportDialog = true"
            prepend-icon="mdi-download"
            class="mr-2"
          >
            Export
          </v-btn>
          <v-btn
            text
            @click="showImportDialog = true"
            prepend-icon="mdi-upload"
            class="mr-2"
          >
            Import
          </v-btn>
          <v-menu>
            <template v-slot:activator="{ props }">
              <v-badge v-if="authStore.user?.role === 'ADMIN'" 
                       content="A" 
                       color="warning" 
                       overlap>
                <v-btn v-bind="props" icon="mdi-account-circle"></v-btn>
              </v-badge>
              <v-btn v-else v-bind="props" icon="mdi-account-circle"></v-btn>
            </template>
            <v-list>
              <v-list-item v-if="authStore.user?.role === 'ADMIN'" @click="$router.push('/admin')">
                <v-list-item-title>
                  <v-icon class="me-2">mdi-shield-account</v-icon>
                  Admin Panel
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="$router.push('/profile')">
                <v-list-item-title>
                  <v-icon class="me-2">mdi-account</v-icon>
                  Profile
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="logout">
                <v-list-item-title>
                  <v-icon class="me-2">mdi-logout</v-icon>
                  Logout
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>
    </v-app-bar>

    <v-main>
      <!-- Breadcrumb Navigation -->
      <BreadcrumbNav v-if="authStore.isAuthenticated && !isSharedView" class="px-4 pt-2" />
      
      <router-view />
    </v-main>

    <!-- Export Dialog -->
    <v-dialog v-model="showExportDialog" max-width="600">
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-download</v-icon>
          Export Data
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" class="mb-4">
            <v-alert-title>Export Information</v-alert-title>
            Your data will be exported as JSON. Image files are referenced by URL only, not included as binary data.
          </v-alert>
          
          <v-card variant="outlined" class="mb-4">
            <v-card-title class="text-subtitle-1">Select Lists to Export</v-card-title>
            <v-card-text>
              <v-checkbox
                v-model="exportAllPacklists"
                label="Export all lists"
                @change="onExportAllChange"
              ></v-checkbox>
              
              <div v-if="!exportAllPacklists && listStore.lists && listStore.lists.length > 0">
                <v-checkbox
                  v-for="list in listStore.lists"
                  :key="list.id"
                  v-model="selectedPacklistIds"
                  :value="list.id"
                  :label="getPacklistDisplayName(list)"
                  class="ml-4"
                ></v-checkbox>
              </div>
              
              <v-alert v-if="!listStore.lists || listStore.lists.length === 0" type="warning" variant="tonal" class="mt-2">
                You don't have any lists to export.
              </v-alert>
            </v-card-text>
          </v-card>
          
          <v-card variant="outlined">
            <v-card-title class="text-subtitle-1">Additional Options</v-card-title>
            <v-card-text>
              <v-checkbox
                v-model="includeSharedLinks"
                label="Include shared links"
                hint="Export shared link URLs and permissions (passwords are not included for security)"
                persistent-hint
              ></v-checkbox>
            </v-card-text>
          </v-card>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showExportDialog = false" :disabled="isExporting">Cancel</v-btn>
          <v-btn 
            color="primary" 
            @click="performExport"
            :loading="isExporting"
            :disabled="!exportAllPacklists && selectedPacklistIds.length === 0"
            prepend-icon="mdi-download"
          >
            Export JSON
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Import Dialog -->
    <v-dialog v-model="showImportDialog" max-width="600">
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-upload</v-icon>
          Import Data
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" class="mb-4">
            <v-alert-title>Import Information</v-alert-title>
            Upload a JSON file exported from this application. Choose how to handle existing data.
          </v-alert>
          
          <v-card variant="outlined" class="mb-4">
            <v-card-title class="text-subtitle-1">Select Import File</v-card-title>
            <v-card-text>
              <v-file-input
                v-model="importFile"
                label="Choose JSON file to import"
                accept=".json"
                prepend-icon="mdi-file-document"
                :rules="[v => !!v || 'File is required', v => !v || v.name.endsWith('.json') || 'Must be a JSON file']"
                clearable
              ></v-file-input>
            </v-card-text>
          </v-card>
          
          <v-card variant="outlined" class="mb-4">
            <v-card-title class="text-subtitle-1">Import Mode</v-card-title>
            <v-card-text>
              <v-radio-group v-model="importMode" class="mt-0">
                <v-radio value="create_new">
                  <template v-slot:label>
                    <div>
                      <strong>Create New</strong>
                      <div class="text-caption text-grey">
                        Ignore all IDs and create new entities. Use this to duplicate data or import from another system.
                      </div>
                    </div>
                  </template>
                </v-radio>
                <v-radio value="update_existing">
                  <template v-slot:label>
                    <div>
                      <strong>Update Existing</strong>
                      <div class="text-caption text-grey">
                        Use IDs to update existing entities. Create new ones if IDs don't exist. Use this to sync changes back.
                      </div>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-card-text>
          </v-card>
          
          <v-alert v-if="importMode === 'update_existing'" type="warning" variant="tonal" class="mb-4">
            <v-alert-title>Update Mode Warning</v-alert-title>
            This will modify your existing data. Make sure you have a backup or export before proceeding.
          </v-alert>
          
          <div v-if="importResult" class="mt-4">
            <v-card variant="outlined" :color="importResult.summary.errors.length > 0 ? 'warning' : 'success'">
              <v-card-title class="text-subtitle-1">
                <v-icon class="mr-2" :color="importResult.summary.errors.length > 0 ? 'warning' : 'success'">
                  {{ importResult.summary.errors.length > 0 ? 'mdi-alert' : 'mdi-check-circle' }}
                </v-icon>
                Import Results
              </v-card-title>
              <v-card-text>
                <v-row dense>
                  <v-col cols="6">
                    <div class="text-caption text-grey">Lists</div>
                    <div>{{ importResult.summary.lists_created + importResult.summary.lists_updated }} / {{ importResult.summary.lists_processed }}</div>
                  </v-col>
                  <v-col cols="6">
                    <div class="text-caption text-grey">Groups</div>
                    <div>{{ importResult.summary.groups_created + importResult.summary.groups_updated }} / {{ importResult.summary.groups_processed }}</div>
                  </v-col>
                  <v-col cols="6">
                    <div class="text-caption text-grey">Items</div>
                    <div>{{ importResult.summary.items_created + importResult.summary.items_updated }} / {{ importResult.summary.items_processed }}</div>
                  </v-col>
                  <v-col cols="6" v-if="importResult.summary.shared_links_processed > 0">
                    <div class="text-caption text-grey">Shared Links</div>
                    <div>{{ importResult.summary.shared_links_created + importResult.summary.shared_links_updated }} / {{ importResult.summary.shared_links_processed }}</div>
                  </v-col>
                </v-row>
                
                <div v-if="importResult.summary.errors.length > 0" class="mt-3">
                  <v-divider class="mb-2"></v-divider>
                  <div class="text-caption text-error mb-2">Errors ({{ importResult.summary.errors.length }}):</div>
                  <div class="text-caption" v-for="(error, index) in importResult.summary.errors.slice(0, 3)" :key="index">
                    • {{ error }}
                  </div>
                  <div v-if="importResult.summary.errors.length > 3" class="text-caption text-grey">
                    ... and {{ importResult.summary.errors.length - 3 }} more errors
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="closeImportDialog" :disabled="isImporting">
            {{ importResult ? 'Close' : 'Cancel' }}
          </v-btn>
          <v-btn 
            v-if="!importResult"
            color="primary" 
            @click="performImport"
            :loading="isImporting"
            :disabled="!importFile || !importMode"
            prepend-icon="mdi-upload"
          >
            Import Data
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create List Dialog -->
    <ListDialog
      v-model="showCreateListDialog"
      :initial-data="null"
      @created="handleListCreated"
      @message="handleMessage"
    />

    <!-- Export Success Snackbar -->
    <v-snackbar v-model="exportSnackbar" :color="exportSnackbarColor" timeout="4000">
      {{ exportSnackbarText }}
      <template v-slot:actions>
        <v-btn variant="text" @click="exportSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from './stores/auth'
import { useListStore } from './stores/list'
import BreadcrumbNav from './components/BreadcrumbNav.vue'
import ListDialog from './components/ListDialog.vue'
import axios from 'axios'

export default {
  name: 'App',
  components: {
    BreadcrumbNav,
    ListDialog
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const authStore = useAuthStore()
    const listStore = useListStore()

    // Create list dialog state
    const showCreateListDialog = ref(false)

    // Export dialog state
    const showExportDialog = ref(false)
    const exportAllPacklists = ref(true)
    const selectedPacklistIds = ref([])
    const includeSharedLinks = ref(false)
    const isExporting = ref(false)
    
    // Import dialog state
    const showImportDialog = ref(false)
    const importFile = ref(null)
    const importMode = ref('create_new')
    const isImporting = ref(false)
    const importResult = ref(null)
    
    // Mobile navigation state
    const showMobileMenu = ref(false)
    
    // Export snackbar
    const exportSnackbar = ref(false)
    const exportSnackbarText = ref('')
    const exportSnackbarColor = ref('success')

    // Computed property to detect if we're in a shared view
    const isSharedView = computed(() => {
      return route.path.startsWith('/shared/')
    })

    const getHeaderTitle = computed(() => {
      if (isSharedView.value) {
        return listStore.sharedLinkName || 'Shared List'
      }
      return 'My Lists'
    })

    const logout = () => {
      authStore.logout()
    }

    const handleMobileLogout = () => {
      showMobileMenu.value = false
      logout()
    }

    const goToPacklists = () => {
      if (authStore.isAuthenticated) {
        showMobileMenu.value = false
        router.push('/lists')
      }
    }

    const openPrintView = () => {
      if (isSharedView.value) {
        // For shared views, trigger the print dialog via store
        listStore.triggerPrintDialog()
      }
      showMobileMenu.value = false
    }

    const fetchListsIfNeeded = async () => {
      if (!authStore.isAuthenticated) return
      
      // Use the existing list store
      if (!listStore.lists || listStore.lists.length === 0) {
        await listStore.fetchLists()
      }
    }

    const handleListCreated = async (list) => {
      // Refresh the lists after creation and close dialog
      await fetchListsIfNeeded()
      showCreateListDialog.value = false
    }

    const handleMessage = (messageData) => {
      // Display messages as snackbar
      exportSnackbarText.value = messageData.message
      exportSnackbarColor.value = messageData.type === 'error' ? 'error' : 'success'
      exportSnackbar.value = true
    }

    const getPacklistDisplayName = (list) => {
      if (!list) return ''
      return list.name
    }

    const onExportAllChange = () => {
      if (exportAllPacklists.value) {
        selectedPacklistIds.value = []
      }
    }

    const performExport = async () => {
      if (!exportAllPacklists.value && selectedPacklistIds.value.length === 0) {
        return
      }

      isExporting.value = true
      
      try {
        // Build query parameters
        const params = new URLSearchParams()
        
        if (!exportAllPacklists.value && selectedPacklistIds.value.length > 0) {
          params.append('packlist_ids', selectedPacklistIds.value.join(','))
        }
        
        if (includeSharedLinks.value) {
          params.append('include_shared_links', 'true')
        }

        const response = await axios.get('/api/export?' + params.toString())
        
        // Create and download the JSON file
        const dataStr = JSON.stringify(response.data, null, 2)
        const dataBlob = new Blob([dataStr], { type: 'application/json' })
        const url = URL.createObjectURL(dataBlob)
        
        // Create download link
        const link = document.createElement('a')
        link.href = url
        
        // Generate filename
        const timestamp = new Date().toISOString().split('T')[0]
        const listCount = exportAllPacklists.value ? 'all' : selectedPacklistIds.value.length
        link.download = `list-export-${listCount}-lists-${timestamp}.json`
        
        // Trigger download
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        // Show success message
        const exportedCount = exportAllPacklists.value ? listStore.lists.length : selectedPacklistIds.value.length
        const sharedLinksText = includeSharedLinks.value ? ' and shared links' : ''
        exportSnackbarText.value = `Successfully exported ${exportedCount} list${exportedCount !== 1 ? 's' : ''}${sharedLinksText}!`
        exportSnackbarColor.value = 'success'
        exportSnackbar.value = true
        
        // Close dialog
        showExportDialog.value = false
        
        // Reset form
        exportAllPacklists.value = true
        selectedPacklistIds.value = []
        includeSharedLinks.value = false
        
      } catch (error) {
        console.error('Export failed:', error)
        exportSnackbarText.value = error.response?.data?.detail || 'Export failed. Please try again.'
        exportSnackbarColor.value = 'error'
        exportSnackbar.value = true
      } finally {
        isExporting.value = false
      }
    }

    // Watch for export dialog opening to load lists
    watch(showExportDialog, async (isOpen) => {
      if (isOpen && authStore.isAuthenticated) {
        await fetchListsIfNeeded()
      }
    })

    const performImport = async () => {
      if (!importFile.value || !importMode.value) return

      isImporting.value = true
      importResult.value = null
      
      try {
        const formData = new FormData()
        formData.append('file', importFile.value)
        formData.append('mode', importMode.value)

        const response = await axios.post('/api/import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        
        importResult.value = response.data
        
        // Refresh lists to show imported data
        await listStore.fetchLists()
        
        exportSnackbarText.value = `Import completed! ${response.data.summary.lists_created + response.data.summary.lists_updated} lists processed.`
        exportSnackbarColor.value = response.data.summary.errors.length > 0 ? 'warning' : 'success'
        exportSnackbar.value = true
        
      } catch (error) {
        console.error('Import failed:', error)
        exportSnackbarText.value = error.response?.data?.detail || 'Import failed. Please check your file and try again.'
        exportSnackbarColor.value = 'error'
        exportSnackbar.value = true
        
        importResult.value = {
          mode: importMode.value,
          summary: {
            lists_processed: 0,
            lists_created: 0,
            lists_updated: 0,
            groups_processed: 0,
            groups_created: 0,
            groups_updated: 0,
            items_processed: 0,
            items_created: 0,
            items_updated: 0,
            shared_links_processed: 0,
            shared_links_created: 0,
            shared_links_updated: 0,
            errors: [error.response?.data?.detail || 'Import failed']
          }
        }
      } finally {
        isImporting.value = false
      }
    }

    const closeImportDialog = () => {
      showImportDialog.value = false
      // Reset form after a short delay to allow dialog to close smoothly
      setTimeout(() => {
        importFile.value = null
        importMode.value = 'create_new'
        importResult.value = null
      }, 300)
    }

    // Initialize lists on mount if authenticated
    onMounted(() => {
      if (authStore.isAuthenticated) {
        fetchListsIfNeeded()
      }
    })

    return {
      authStore,
      logout,
      handleMobileLogout,
      goToPacklists,
      openPrintView,
      isSharedView,
      getHeaderTitle,
      // Create list dialog
      showCreateListDialog,
      handleListCreated,
      handleMessage,
      // Export functionality
      showExportDialog,
      exportAllPacklists,
      selectedPacklistIds,
      includeSharedLinks,
      isExporting,
      exportSnackbar,
      exportSnackbarText,
      exportSnackbarColor,
      listStore,
      fetchListsIfNeeded,
      getPacklistDisplayName,
      onExportAllChange,
      performExport,
      // Import functionality
      showImportDialog,
      importFile,
      importMode,
      isImporting,
      importResult,
      performImport,
      closeImportDialog,
      // Mobile navigation
      showMobileMenu
    }
  }
}
</script>

<style>
@media print {
  .v-app-bar,
  .v-toolbar {
    display: none !important;
  }
  
  .v-main {
    padding-top: 0 !important;
    margin-top: 0 !important;
  }
  
  .v-app {
    padding-top: 0 !important;
  }
}
</style>